import { useState, useRef } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { trackEvent, attributionForSubmit } from "@/lib/analytics";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const formStarted = useRef(false);
  const update = (k) => (e) => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent("contact_form_started", { form_name: "contact-section" });
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, attribution: attributionForSubmit() });
      trackEvent("contact_form_submitted", { form_name: "contact-section" });
      toast.success("Message sent — we'll get back to you within 1 business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-[#070707] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00] mb-4">
              Get In Touch
            </div>
            <h2 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tight text-balance leading-[0.95]">
              Let's <br />
              <span className="text-[#FF6B00]">Talk</span>
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-md">
              Press, partnerships, support, distribution &mdash; we read everything.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-[#FF6B00]" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-white/50">
                    Email
                  </div>
                  <a
                    href="mailto:jcd@itspicklewall.com"
                    className="text-white hover:text-[#FF6B00] transition-colors"
                    onClick={() => trackEvent("email_click", { button_location: "contact-section" })}
                  >
                    jcd@itspicklewall.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-[#FF6B00]" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-white/50">
                    HQ
                  </div>
                  <div className="text-white">Montverde, FL 34756 · United States</div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid={CONTACT.form}
            className="lg:col-span-3 glass rounded-3xl p-8 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="sr-only">Name</span>
                <input
                  data-testid={CONTACT.name}
                  placeholder="Name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm"
                />
              </label>
              <label className="block">
                <span className="sr-only">Email</span>
                <input
                  data-testid={CONTACT.email}
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm"
                />
              </label>
            </div>
            <label className="block mt-4">
              <span className="sr-only">Subject</span>
              <input
                data-testid={CONTACT.subject}
                placeholder="Subject"
                value={form.subject}
                onChange={update("subject")}
                className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm"
              />
            </label>
            <label className="block mt-4">
              <span className="sr-only">Your message</span>
              <textarea
                data-testid={CONTACT.message}
                placeholder="Your message..."
                rows={6}
                value={form.message}
                onChange={update("message")}
                required
                className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid={CONTACT.submit}
              className="mt-6 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 transition-all hover:orange-glow"
            >
              {loading ? "Sending..." : "Send Message"}
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
